import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import {
  PaymentDetails,
  ProfileDetails,
  saveBrandProfile,
  savePaymentConfiguration,
} from "../services/brandService";
import type { BrandDetails } from "../types/brand.types";

/**
 * What landed. Each half is the record as the backend now holds it, or `null`
 * where that call failed — the page commits only what came back, so a failed
 * half stays dirty and is what the next Save retries.
 */
export interface BrandSaveResult {
  profile: ProfileDetails | null;
  payment: PaymentDetails | null;
}

export interface BrandSave {
  /** True while either call is in flight; the Save button reads it. */
  saving: boolean;
  save: (details: BrandDetails) => Promise<BrandSaveResult>;
}

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

/**
 * Saves the My brand form: both records at once, reported one at a time.
 *
 * They are separate endpoints over separate records, so `allSettled` rather
 * than `all` — one failing must not throw away what the other one stored, and
 * the message has to name the half that did not land or there is no telling
 * what is still unsaved.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useBrandSave(): BrandSave {
  const { getToken } = useAuth();
  const toast = useToast();

  // Clerk hands back a fresh `getToken` on some renders, and this hook is used
  // by a form that re-renders on every keystroke — so `save` reads the current
  // one through a ref rather than depending on its identity.
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  const [saving, setSaving] = useState(false);

  // Nothing to abort on unmount: a save that is already with the server should
  // finish. This only stops the result being written back into a gone form.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const save = useCallback(
    async (details: BrandDetails): Promise<BrandSaveResult> => {
      setSaving(true);

      const getCurrentToken = () => getTokenRef.current();

      const [profileResult, paymentResult] = await Promise.allSettled([
        saveBrandProfile(getCurrentToken, details),
        savePaymentConfiguration(getCurrentToken, details),
      ]);

      if (mounted.current) setSaving(false);

      if (profileResult.status === "rejected") {
        console.error("brand profile failed to save:", profileResult.reason);
        toast.error(
          "Could not save your brand details",
          failureMessage(profileResult.reason)
        );
      }

      if (paymentResult.status === "rejected") {
        console.error(
          "payment configuration failed to save:",
          paymentResult.reason
        );
        toast.error(
          "Could not save your payment details",
          failureMessage(paymentResult.reason)
        );
      }

      // One confirmation, and only when the whole form is stored. Saying so
      // while half of it failed would be worse than saying nothing.
      if (
        profileResult.status === "fulfilled" &&
        paymentResult.status === "fulfilled"
      ) {
        toast.success(
          "Brand saved",
          "Your brand and payment details are up to date."
        );
      }

      return {
        profile:
          profileResult.status === "fulfilled" ? profileResult.value : null,
        payment:
          paymentResult.status === "fulfilled" ? paymentResult.value : null,
      };
    },
    [toast]
  );

  return { saving, save };
}
